import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

import { useAdmin } from '../../context/AdminContext';
import type { AdminStats } from '../../services/api';
import api from '../../services/api';

import styles from './AdminDashboard.module.css';

/**
 * AdminDashboard Component
 * Displays admin statistics and quick action buttons
 */

const AdminDashboard = (): JSX.Element => {
  const { user } = useAdmin();
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const data = await api.admin.getStats();
      setStats(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching admin stats:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.dashboard}>
      <div className={styles.header}>
        <h1 className={styles.title}>Admin Dashboard</h1>
        <p className={styles.welcome}>Welcome, {user?.username}!</p>
      </div>

      {loading ? (
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Loading statistics...</p>
        </div>
      ) : null}

      {error ? (
        <div className={styles.error}>
          <p>❌ {error}</p>
          <button onClick={fetchStats} className={styles.retryButton}>
            Retry
          </button>
        </div>
      ) : null}

      {!loading && !error && stats ? (
        <>
          {/* Statistics Cards */}
          <section className={styles.statsSection}>
            <h2 className={styles.sectionTitle}>Statistics</h2>
            <div className={styles.statsGrid}>
              <div className={styles.statCard}>
                <div className={styles.statIcon}>🖼️</div>
                <div className={styles.statContent}>
                  <div className={styles.statValue}>{stats.images}</div>
                  <div className={styles.statLabel}>Images</div>
                </div>
              </div>

              <div className={styles.statCard}>
                <div className={styles.statIcon}>📄</div>
                <div className={styles.statContent}>
                  <div className={styles.statValue}>{stats.pages}</div>
                  <div className={styles.statLabel}>Pages</div>
                </div>
              </div>

              <div className={styles.statCard}>
                <div className={styles.statIcon}>🏷️</div>
                <div className={styles.statContent}>
                  <div className={styles.statValue}>{stats.categories}</div>
                  <div className={styles.statLabel}>Categories</div>
                </div>
              </div>

              <div className={styles.statCard}>
                <div className={styles.statIcon}>📋</div>
                <div className={styles.statContent}>
                  <div className={styles.statValue}>{stats.menuItems}</div>
                  <div className={styles.statLabel}>Menu Items</div>
                </div>
              </div>
            </div>
          </section>

          {/* Quick Actions */}
          <section className={styles.actionsSection}>
            <h2 className={styles.sectionTitle}>Quick Actions</h2>
            <div className={styles.actionsGrid}>
              <Link to="/admin/menu" className={styles.actionButton}>
                <span className={styles.actionIcon}>📋</span>
                <span className={styles.actionLabel}>Manage Menu</span>
              </Link>

              <Link to="/admin/content" className={styles.actionButton}>
                <span className={styles.actionIcon}>✏️</span>
                <span className={styles.actionLabel}>Edit Content</span>
              </Link>

              <Link to="/admin/images" className={styles.actionButton}>
                <span className={styles.actionIcon}>📸</span>
                <span className={styles.actionLabel}>Upload Images</span>
              </Link>

              <Link to="/admin/categories" className={styles.actionButton}>
                <span className={styles.actionIcon}>🏷️</span>
                <span className={styles.actionLabel}>Manage Categories</span>
              </Link>
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
};

export default AdminDashboard;
